import { useState } from "react";
import appearanceFavicon from "../../assets/appearance-favicon.png";
import { api } from "../../state/api";
import {
  BrandMode,
  type AppConfigurationResponse,
  type AppResponse,
} from "../../state/api.types";
import { useMutationWithToast } from "../../utilities/useMutationWithToast";
import { FormCard } from "../shared/FormCard";
import { BrandModeLockedNotice } from "./BrandModeLockedNotice";
import { FaviconPreview } from "./FaviconPreview";
import { ImageDrop } from "./ImageDrop";

const ICON_SUBTEXT =
  "Upload a square transparent PNG of your logomark to show up in the browser tab of no-code experiences.";

type AppearanceFaviconCardProps = {
  app: AppResponse;
  configuration: AppConfigurationResponse;
};

export const AppearanceFaviconCard = (props: AppearanceFaviconCardProps) => {
  const [updateConfigurationFaviconLogo, updateConfigurationFaviconLogoResult] =
    useMutationWithToast(api.useUpdateConfigurationFaviconLogoMutation(), {
      successMessage: "The app's icon has been updated",
    });

  const [file, setFile] = useState<File | null>(null);
  const [src, setSrc] = useState(props.configuration.favicon_logo);

  if (props.configuration.brand_mode === BrandMode.Subtle) {
    return (
      <FormCard name="Icon" subtext={ICON_SUBTEXT}>
        <BrandModeLockedNotice />
      </FormCard>
    );
  }

  return (
    <FormCard
      name="Icon"
      subtext={ICON_SUBTEXT}
      textCancel="Reset"
      buttonText="Save image"
      isDisabled={!file}
      isLoading={updateConfigurationFaviconLogoResult.isLoading}
      onCancel={() => {
        setFile(null);
        setSrc(props.configuration.favicon_logo);
      }}
      onButtonClick={() => {
        if (file) {
          updateConfigurationFaviconLogo({
            appUUID: props.app.uuid,
            favicon_logo: file,
          });
        }
      }}
      data-ph-capture-attribute-action="update-favicon"
    >
      <div className="grid grid-cols-2 gap-3xl">
        <ImageDrop
          className="size-full"
          height={40}
          width={40}
          src={src}
          isDisabled={updateConfigurationFaviconLogoResult.isLoading}
          onFile={(f) => {
            setFile(f);
            setSrc(URL.createObjectURL(f));
          }}
        />
        <FaviconPreview className="size-full" src={src ?? appearanceFavicon} />
      </div>
    </FormCard>
  );
};
